import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { useHistory } from 'react-router-dom'

import { connectApi } from 'libs/connectApi'
import { toast } from 'libs/toast'
import { updateSurveyFromId } from 'actions'
import { SurveyConfigOptions } from '.'
import useAxios, { mutate, cache } from 'hooks/useAxios'
import useSaveUserActivityLog, {
  ACTION_NAME,
  SURVEY_STATUS_ACTION_NAME,
} from 'hooks/useSaveUserActivityLog'
import { useNavbarMenuContext } from 'organisms/NavBar/context'

const SurveyConfigOptionsContainer = ({ survey, updateSurvey, updateSurveyFromId }) => {
  const history = useHistory()
  const { updateContextData } = useNavbarMenuContext()
  const saveUserActivityLog = useSaveUserActivityLog()
  const { fetch: duplicateSurveyAPI, loading: duplicateSurveyLoading } = useAxios({
    method: 'post',
    url: `survey/${survey.token}/duplicate/`,
    skip: true,
  })

  function updateStatus(status) {
    return updateSurvey
      .callApi({ data: { status } })
      .then((response) => {
        if (response.status === 'active') {
          toast.success('Your survey has been unarchived.')
        }
        if (response.status === 'archived') {
          toast.success('Your survey has been archived.')
          history.push('/settings/surveys/')
        }
        saveUserActivityLog({
          action: SURVEY_STATUS_ACTION_NAME[status],
          target: `sur_${survey.id}`,
          requestBody: { status },
          statusCode: 200,
        })
        updateSurveyFromId(survey.id, { status: response.status })
        updateContextData()
        mutate('dashboard/list-favorite-view/?type=dashboard&type=survey')
      })
      .catch((err) => {
        toast.error('Oops, there was an error saving your survey. Please try again.')
        saveUserActivityLog({
          action: SURVEY_STATUS_ACTION_NAME[status],
          target: `sur_${survey.id}`,
          requestBody: { status },
          statusCode: err.status,
        })
        throw err
      })
  }

  function createOnRemoveDeliveryMethod(onDeliveryMethodRemoved) {
    return () => {
      const noDeliveryMethodSurveyType = 'n/a'
      updateSurvey
        .callApi({ data: { type: noDeliveryMethodSurveyType, connector: null } })
        .then((data) => {
          mutate(`account/survey/${survey.id}/`, data, false)
          toast.success('Your delivery method has been removed.')
          updateSurveyFromId(survey.id, { type: noDeliveryMethodSurveyType })
          onDeliveryMethodRemoved()
          history.push(`/settings/surveys/${survey.id}/publish/`)
        })
        .catch((err) => {
          toast.error('Oops, there was an error saving your survey. Please try again.')
          throw err
        })
    }
  }

  function flipChoicesOrder(isReverseOrder) {
    const settings = {
      ...survey.settings,
      setups: { ...(survey.settings || {}).setups, choiceOrder: isReverseOrder },
    }
    return updateSurvey
      .callApi({
        data: { settings },
        config: { headers: { 'X-Survey-Token': survey.token } },
      })
      .then((data) => {
        mutate(`account/survey/${survey.id}/`, data, false)
        // Revalidate the Publish → Email template preview cache — the BE renders
        // the template HTML with the current `settings.setups.choiceOrder` baked
        // into the sorted `choices` context, so the preview must re-fetch to
        // reflect the flip. SWR 0.3.2 has no predicate/matcher mutate (a function
        // key is invoked, not matched), so enumerate the cache and revalidate each
        // email-templates entry individually — this also covers `?language=`
        // variants.
        const emailTemplatesPrefix = `survey/survey/${survey.token}/email-templates/`
        cache
          .keys()
          .filter((key) => typeof key === 'string' && key.startsWith(emailTemplatesPrefix))
          .forEach((key) => mutate(key))
        updateSurveyFromId(survey.id, { settings })
        toast.success('Choice order updated.')
      })
      .catch((err) => {
        toast.error('Oops, there was an error saving your survey. Please try again.')
        throw err
      })
  }

  function duplicateSurvey(surveyName) {
    const requestData = { survey_name: surveyName }
    duplicateSurveyAPI({
      data: requestData,
    })
      .then((response) => {
        toast.success('Success! A new duplicate survey has been created.')
        saveUserActivityLog({
          action: ACTION_NAME.duplicate_survey,
          target: `sur_${survey.id}`,
          requestBody: requestData,
          statusCode: response.status,
        })
        history.push(`/settings/surveys/${response.data.id}/settings/`)
        mutate('account/survey/?limit=5000&order_by=asc')
        updateContextData()
      })
      .catch((error) => {
        toast.error(
          'Whoops! Something went wrong. Please try again and contact Simplesat support if you continue to see any issues.'
        )
        saveUserActivityLog({
          action: ACTION_NAME.duplicate_survey,
          target: `sur_${survey.id}`,
          requestBody: requestData,
          statusCode: error.response.status,
        })
      })
  }

  return (
    <SurveyConfigOptions
      survey={survey}
      handleUpdateSurveyStatus={updateStatus}
      createOnRemoveDeliveryMethod={createOnRemoveDeliveryMethod}
      updateSurveyLoading={updateSurvey.loading}
      duplicateSurvey={duplicateSurvey}
      duplicateSurveyLoading={duplicateSurveyLoading}
      flipChoicesOrder={flipChoicesOrder}
      flipChoicesOrderLoading={updateSurvey.loading}
      isReverseOrder={Boolean(
        survey.settings && survey.settings.setups && survey.settings.setups.choiceOrder
      )}
    />
  )
}

SurveyConfigOptionsContainer.propTypes = {
  survey: PropTypes.object,
  updateSurvey: PropTypes.object,
  updateSurveyFromId: PropTypes.func,
}

const mapDispatch = (dispatch) => ({
  updateSurveyFromId: (surveyId, settings) => dispatch(updateSurveyFromId(surveyId, settings)),
})

export default compose(
  connect(null, mapDispatch),
  connectApi({
    name: 'updateSurvey',
    url: ({ survey }) => `account/survey/${survey.id}/`,
    method: 'patch',
    skip: true,
  })
)(SurveyConfigOptionsContainer)



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/SurveyConfigOptions/container.js